import { useForm as createUseForm } from "@ant-design-vue/use";
import lodash from "lodash";
import { defineAsyncComponent, reactive, toRaw } from "vue";
import { addModal } from "../../../../../app.logic";
import { createFieldRadioGroup, createFieldSelect } from "../../../../../components/input/template-builder/template-builder.logic";
import i18n from "../../../../../i18n";
import { modalStyleForm } from "../../../../../utils/constant";
import { initUseForm } from "../../../../../utils/form";
import { createLoading } from "../../../../../utils/loading";
import { required, requiredWhen, validateError } from "../../../../../utils/validate";
import { callUserApprove } from "../../../storage-location/request/modal/confirm-receive.logic";
import { callRequestStatus } from "../../../storage-location/request/request.section";
import { callInspectionLv } from "../request.section";

export const { isLoading, setIsLoading } = createLoading();

export const ConfirmReceiveForm = defineAsyncComponent(() => import("./ConfirmReceiveForm"));

export const openModalForm = ({ props, onOk, bind }) => {
    const refs = reactive({ form: null });
    const options = addModal({
        bind: lodash.merge(
            lodash.cloneDeep(modalStyleForm),
            {
                okButtonProps: { disabled: false },
                title: i18n.global.t("views.request.modal.receive.title"),
                width: "500px",
                onOk: () => {
                    refs.form.callOnClickModalOk().then(record => {
                        onOk?.(record);
                        options.bind.onCancel();
                    });
                }
            },
            bind
        ),
        render: () => (
            <ConfirmReceiveForm
                ref={e => {
                    if (e) refs.form = e;
                }}
                {...props}
            ></ConfirmReceiveForm>
        )
    });
    return options;
};

export const onClickModalOk = ({ data }) => {
    return data.useForm
        .validate()
        .then(() => ({ ...toRaw(data.useForm.modelRef) }))
        .catch(error => {
            validateError({ error });
            throw error;
        });
};

export const createForm = () => {
    return {
        refStatus: null,
        refToApproveBy: null,
        refInspectionLv: null
    };
};

export const createRules = ({ props, data }) => {
    return {
        refStatus: [required],
        refToApproveBy: [requiredWhen({ when: () => data.useForm.modelRef.refStatus?.key === "waitApprove" })],
        refInspectionLv: [requiredWhen({ when: () => props.visibleRefInspectionLv && data.useForm.modelRef.refStatus?.key === "waitApprove" })]
    };
};

export const createField = ({ props, data }) => {
    const { useForm } = data;
    const { modelRef } = useForm;
    return [
        createFieldRadioGroup({
            key: "refStatus",
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 24 }, wrapperCol: { xs: 24 } }),
            inputProp: () => {
                return {
                    value: modelRef.refStatus,
                    "onUpdate:value": value => {
                        modelRef.refStatus = value;
                        modelRef.refToApproveBy = null;
                        modelRef.refInspectionLv = null;
                        if (props.visibleRefInspectionLv && value?.key === "waitApprove") {
                            modelRef.refInspectionLv = props.defaultRefInspectionLv;
                        }
                    },
                    list: ({ searchQuery }) =>
                        callRequestStatus({ searchQuery }).then(items => {
                            const result = [];
                            const reEditOfficer = items.find(item => item.key === "reEditOfficer");
                            const reEditNsw = items.find(item => item.key === "reEditNsw");
                            const waitApprove = items.find(item => item.key === "waitApprove");
                            const notApprove = items.find(item => item.key === "notApprove");
                            if (waitApprove) result.push(waitApprove);
                            if (reEditOfficer) result.push(reEditOfficer);
                            if (props.visibleReEditNsw && reEditNsw) result.push(reEditNsw);
                            if (notApprove) result.push(notApprove);
                            return result.filter(d => {
                                if (props.statusKeys?.length > 0) {
                                    return props.statusKeys.includes(d.key);
                                }
                                return true;
                            });
                        }),
                    optLabel: "attribute.actionText",
                    type: "vertical"
                };
            }
        }),
        createFieldSelect({
            key: "refToApproveBy",
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 24 }, wrapperCol: { xs: 24 } }),
            inputProp: () => {
                const disabled = modelRef.refStatus?.key !== "waitApprove";
                return {
                    value: modelRef.refToApproveBy,
                    "onUpdate:value": value => (modelRef.refToApproveBy = value),
                    list: ({ searchQuery }) => callUserApprove({ searchQuery, roleName: props.roleName }),
                    optLabel: "fullNameTh",
                    placeholder: i18n.global.t("views.request.modal.receive.refToApproveBy"),
                    disabled
                };
            }
        }),
        createFieldSelect({
            visible: () => props.visibleRefInspectionLv,
            key: "refInspectionLv",
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 24 }, wrapperCol: { xs: 24 } }),
            inputProp: () => {
                const disabled = modelRef.refStatus?.key !== "waitApprove" || props.disabledRefInspectionLv;
                return {
                    value: modelRef.refInspectionLv,
                    "onUpdate:value": value => (modelRef.refInspectionLv = value),
                    list: callInspectionLv,
                    optLabel: "value",
                    placeholder: i18n.global.t("model.inform.refInspectionLv"),
                    disabled
                };
            }
        })
    ];
};

export const loadForm = ({ props, data }) => {
    setIsLoading(true);
    const { form } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    let promise = null;
    if (form) {
        promise = Promise.resolve({ data: [form] });
    } else {
        promise = Promise.resolve({ data: [modelRef] });
    }
    return promise.then(page => initUseForm({ data, page })).finally(() => setIsLoading(false));
};

export const initForm = ({ props, data, refs }) => {
    data.form = createForm({ props, data, refs });
    data.rules = createRules({ props, data, refs });
    data.useForm = createUseForm(data.form, data.rules);
    data.fields = createField({ props, data, refs });
    loadForm({ props, data, refs });
};
